import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';
import { 
  User, 
  Mail, 
  Phone, 
  Edit, 
  Star, 
  Car, 
  Award, 
  Calendar,
  DollarSign,
  Users,
  Shield as ShieldIcon,
  LogOut,
  CheckCircle,
  TrendingUp,
  Gift,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import RazorpayButton from '../components/RazorpayButton';

const Profile = () => {
  const { logout, user, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    gender: user?.gender || 'prefer_not_to_say',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    aadharNumber: user?.aadharNumber || ''
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [showSuccess, setShowSuccess] = useState(false);
  const [aadharOtp, setAadharOtp] = useState('');
  const [showAadharVerification, setShowAadharVerification] = useState(false);
  const [connectPackages, setConnectPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(user?.profilePicture || '');
  const [uploading, setUploading] = useState(false);
  // Add state for single-field editing
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [buying, setBuying] = useState(false);
  const [buySuccess, setBuySuccess] = useState(false);
  const [buyError, setBuyError] = useState('');

  // Dummy ride statistics
  const rideStats = {
    totalRides: 24,
    totalDistance: 1250,
    totalEarnings: 2450,
    averageRating: 4.8,
    ridesThisMonth: 8,
    moneySaved: 1800,
    carbonSaved: 45
  };

  // Dummy achievements
  const achievements = [
    {
      id: 1,
      title: 'First Ride',
      description: 'Completed your first ride',
      icon: '🚗',
      earned: true,
      date: '2024-01-01'
    },
    {
      id: 2,
      title: 'Regular Commuter',
      description: 'Completed 10 rides',
      icon: '🏆',
      earned: true,
      date: '2024-01-10'
    },
    {
      id: 3,
      title: 'Top Rated',
      description: 'Achieved 4.5+ rating',
      icon: '⭐',
      earned: true,
      date: '2024-01-15'
    },
    {
      id: 4,
      title: 'Eco Warrior',
      description: 'Saved 50kg CO2',
      icon: '🌱',
      earned: false,
      progress: 45
    },
    {
      id: 5,
      title: 'Social Butterfly',
      description: 'Connected with 20+ people',
      icon: '🦋',
      earned: false,
      progress: 15
    }
  ];

  // Dummy recent rides
  const recentRides = [
    {
      id: 1,
      from: 'Mumbai Central',
      to: 'Andheri West',
      date: '2024-01-14',
      time: '09:00 AM',
      price: 120,
      status: 'completed',
      rating: 5
    },
    {
      id: 2,
      from: 'Bandra West',
      to: 'Powai',
      date: '2024-01-13',
      time: '08:30 AM',
      price: 150,
      status: 'completed',
      rating: 4
    },
    {
      id: 3,
      from: 'Thane',
      to: 'BKC',
      date: '2024-01-12',
      time: '07:45 AM',
      price: 200,
      status: 'completed',
      rating: 5
    }
  ];

  useEffect(() => {
    // Fetch connect packages
    fetchConnectPackages();
  }, []);

  const fetchConnectPackages = async () => {
    try {
      const response = await fetchWithAuth('/api/auth/connect-packages');
      const data = await response.json();
      setConnectPackages(data.packages);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleEdit = () => {
    setEditData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      gender: user?.gender || 'prefer_not_to_say',
      dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
      aadharNumber: user?.aadharNumber || ''
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetchWithAuth('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editData)
      }, logout);

      if (response.ok) {
        const updatedUser = await response.json();
        setIsEditing(false);
        setShowSuccess(true);
        setUser(updatedUser.user || updatedUser); // update context
        localStorage.setItem('user', JSON.stringify(updatedUser.user || updatedUser));
        setTimeout(() => setShowSuccess(false), 3000);
        toast.success('Profile updated!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      alert('Failed to update profile');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAadharVerification = async () => {
    try {
      const response = await fetchWithAuth('/api/auth/verify-aadhar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          aadharNumber: editData.aadharNumber,
          otp: aadharOtp
        })
      }, logout);

      if (response.ok) {
        setShowAadharVerification(false);
        setAadharOtp('');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        // Refresh user data
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.error || 'Aadhar verification failed');
      }
    } catch (error) {
      console.error('Aadhar verification error:', error);
      alert('Aadhar verification failed');
    }
  };

  const handleBuyConnects = async () => {
    try {
      const response = await fetchWithAuth('/api/auth/buy-connects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          package: selectedPackage.id,
          paymentMethod: paymentMethod
        })
      }, logout);

      if (response.ok) {
        setShowPaymentModal(false);
        setSelectedPackage(null);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        // Refresh user data
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to purchase connects');
      }
    } catch (error) {
      console.error('Buy connects error:', error);
      alert('Failed to purchase connects');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const getProfileLevel = () => {
    if (rideStats.totalRides >= 50) return { level: 'Platinum', color: 'text-purple-600' };
    if (rideStats.totalRides >= 25) return { level: 'Gold', color: 'text-yellow-600' };
    if (rideStats.totalRides >= 10) return { level: 'Silver', color: 'text-gray-600' };
    return { level: 'Bronze', color: 'text-orange-600' };
  };

  const profileLevel = getProfileLevel();

  const getGenderLabel = (gender) => {
    switch (gender) {
      case 'male': return 'Male';
      case 'female': return 'Female';
      case 'other': return 'Other';
      case 'prefer_not_to_say': return 'Prefer not to say';
      default: return 'Not specified';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSavePhoto = async () => {
    if (!selectedFile) return;
    setUploading(true);
    // In dev mode, just update the user context (local only)
    const updatedUser = { ...user, profilePicture: preview };
    setUser(updatedUser); // Update context so sidebar/navbar re-render
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUploading(false);
    toast.success('Profile photo updated! (Local only, not saved to server)');
  };

  // Add a function to start editing a field
  const startEdit = (field, value) => {
    setEditingField(field);
    setFieldValue(value);
  };

  // Add a function to save a single field
  const saveField = async () => {
    try {
      const response = await fetchWithAuth('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ [editingField]: fieldValue })
      }, logout);
      if (response.ok) {
        const updatedUser = await response.json();
        setEditingField(null);
        setShowSuccess(true);
        setUser(updatedUser.user || updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser.user || updatedUser));
        setTimeout(() => setShowSuccess(false), 3000);
        toast.success('Profile updated!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update profile');
      }
    } catch (error) {
      alert('Failed to update profile');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-bounce">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>Operation completed successfully!</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile</h1>
        <p className="text-gray-600">Manage your account and view your ride statistics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white text-center">
              <div className="relative inline-block">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src={preview || '/default-avatar.png'} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                </div>
                {user?.aadharVerified && (
                  <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1">
                    <ShieldIcon className="w-4 h-4 text-white" />
                  </div>
                )}
                {/* Change Profile Photo Button */}
                <div className="mt-2 flex flex-col items-center">
                  <label htmlFor="profile-photo-upload" className="cursor-pointer text-blue-200 hover:text-white text-xs underline">
                    Change Profile Photo
                  </label>
                  <input
                    id="profile-photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {selectedFile && (
                    <button
                      onClick={handleSavePhoto}
                      disabled={uploading}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      {uploading ? 'Saving...' : 'Save Photo'}
                    </button>
                  )}
                </div>
              </div>
              <h2 className="text-xl font-semibold mb-1">{user?.name || 'User'}</h2>
              <p className="text-blue-100 text-sm mb-3">{user?.phone || 'No phone'}</p>
              <div className="flex items-center justify-center space-x-2">
                <Star className="w-4 h-4 text-yellow-300" />
                <span className="text-sm">{rideStats.averageRating}</span>
                <span className="text-blue-100 text-sm">({rideStats.totalRides} rides)</span>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Award className="w-5 h-5 text-purple-600 mr-2" />
                  <span className="font-semibold">Profile Level</span>
                </div>
                <span className={`font-bold ${profileLevel.color}`}>{profileLevel.level}</span>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="font-semibold">Connects</span>
                </div>
                <span className="font-bold text-green-600">{user?.connects || 0}</span>
              </div>

              {user?.aadharVerified && (
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <ShieldIcon className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-semibold">Aadhar Verified</span>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleEdit}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Full Profile Edit Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 w-full max-w-lg shadow-lg relative">
              <h2 className="text-2xl font-bold mb-6 text-center">Edit Profile</h2>
              <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    name="gender"
                    value={editData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="prefer_not_to_say">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={editData.dateOfBirth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                  <input
                    type="text"
                    name="aadharNumber"
                    value={editData.aadharNumber}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '');
                      setEditData(prev => ({ ...prev, aadharNumber: value }));
                    }}
                    maxLength={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </form>
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none"
                onClick={handleCancel}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-lg mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: User },
                  { id: 'personal', label: 'Personal Info', icon: User },
                  { id: 'aadhar', label: 'Aadhar Verification', icon: ShieldIcon },
                  { id: 'connects', label: 'Buy Connects', icon: Gift },
                  { id: 'achievements', label: 'Achievements', icon: Award },
                  { id: 'rides', label: 'Recent Rides', icon: Car }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{rideStats.totalRides}</div>
                      <div className="text-sm text-gray-600">Total Rides</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{rideStats.totalDistance}km</div>
                      <div className="text-sm text-gray-600">Distance</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">₹{rideStats.totalEarnings}</div>
                      <div className="text-sm text-gray-600">Earnings</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-yellow-600">{rideStats.averageRating}</div>
                      <div className="text-sm text-gray-600">Rating</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-r from-green-400 to-blue-500 p-6 rounded-lg text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Money Saved</h3>
                          <p className="text-3xl font-bold">₹{rideStats.moneySaved}</p>
                          <p className="text-green-100">vs. solo rides</p>
                        </div>
                        <DollarSign className="w-12 h-12 text-green-100" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-400 to-pink-500 p-6 rounded-lg text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Carbon Saved</h3>
                          <p className="text-3xl font-bold">{rideStats.carbonSaved}kg</p>
                          <p className="text-purple-100">CO2 emissions</p>
                        </div>
                        <TrendingUp className="w-12 h-12 text-purple-100" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Info Tab */}
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    {editingField === 'name' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={fieldValue}
                          onChange={e => setFieldValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button onClick={saveField} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                        <button onClick={() => setEditingField(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{user.name}</span>
                        <button onClick={() => startEdit('name', user.name)} className="text-blue-600 underline text-xs">Edit</button>
                      </div>
                    )}
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    {editingField === 'email' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={fieldValue}
                          onChange={e => setFieldValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button onClick={saveField} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                        <button onClick={() => setEditingField(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{user.email || 'Not set'}</span>
                        <button onClick={() => startEdit('email', user.email || '')} className="text-blue-600 underline text-xs">Edit</button>
                      </div>
                    )}
                  </div>
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    {editingField === 'gender' ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={fieldValue}
                          onChange={e => setFieldValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="prefer_not_to_say">Prefer not to say</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        <button onClick={saveField} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                        <button onClick={() => setEditingField(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{getGenderLabel(user.gender)}</span>
                        <button onClick={() => startEdit('gender', user.gender || 'prefer_not_to_say')} className="text-blue-600 underline text-xs">Edit</button>
                      </div>
                    )}
                  </div>
                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    {editingField === 'dateOfBirth' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={fieldValue}
                          onChange={e => setFieldValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button onClick={saveField} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                        <button onClick={() => setEditingField(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : 'Not set'}</span>
                        <button onClick={() => startEdit('dateOfBirth', user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '')} className="text-blue-600 underline text-xs">Edit</button>
                      </div>
                    )}
                  </div>
                  {/* Aadhar Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                    {editingField === 'aadharNumber' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={fieldValue}
                          onChange={e => setFieldValue(e.target.value)}
                          maxLength={12}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button onClick={saveField} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                        <button onClick={() => setEditingField(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{user.aadharNumber || 'Not set'}</span>
                        <button onClick={() => startEdit('aadharNumber', user.aadharNumber || '')} className="text-blue-600 underline text-xs">Edit</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Aadhar Verification Tab */}
              {activeTab === 'aadhar' && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShieldIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Aadhar Verification</h3>
                    <p className="text-gray-600">Verify your identity to unlock premium features</p>
                  </div>

                  {user?.aadharVerified ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center justify-center mb-4">
                        <CheckCircle2 className="w-12 h-12 text-green-600" />
                      </div>
                      <h4 className="text-lg font-semibold text-green-800 text-center mb-2">
                        Verification Complete!
                      </h4>
                      <p className="text-green-700 text-center mb-4">
                        Your Aadhar has been successfully verified. You now have access to all premium features.
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Aadhar Number:</span>
                            <p className="text-gray-900">XXXX XXXX {user?.aadharNumber?.slice(-4) || 'XXXX'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Verification Date:</span>
                            <p className="text-gray-900">Today</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <AlertCircle className="w-6 h-6 text-yellow-600 mr-2" />
                          <h4 className="text-lg font-semibold text-yellow-800">
                            Verification Required
                          </h4>
                        </div>
                        <p className="text-yellow-700 mb-4">
                          Complete Aadhar verification to unlock premium features and enhance your profile trust score.
                        </p>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          <li>• Verified badge on your profile</li>
                          <li>• Access to premium connects</li>
                          <li>• Priority customer support</li>
                          <li>• Enhanced trust score</li>
                        </ul>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Start Verification</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Aadhar Number
                            </label>
                            <input
                              type="text"
                              value={editData.aadharNumber}
                              onChange={(e) => setEditData(prev => ({
                                ...prev,
                                aadharNumber: e.target.value.replace(/\D/g, '')
                              }))}
                              placeholder="Enter 12-digit Aadhar number"
                              maxLength="12"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          
                          <button
                            onClick={() => setShowAadharVerification(true)}
                            disabled={!editData.aadharNumber || editData.aadharNumber.length !== 12}
                            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                          >
                            Start Verification
                          </button>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <ShieldIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Security & Privacy</p>
                            <ul className="space-y-1 text-xs">
                              <li>• Your Aadhar data is encrypted and secure</li>
                              <li>• We only verify your identity, no data is stored</li>
                              <li>• Verification is done through secure UIDAI channels</li>
                              <li>• Your privacy is our top priority</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Buy Connects Tab */}
              {activeTab === 'connects' && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Buy Connects</h3>
                    <p className="text-gray-600">Choose a package that suits your needs</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {connectPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`relative bg-white border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          pkg.popular
                            ? 'border-blue-500 shadow-lg scale-105'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowPaymentModal(true);
                        }}
                      >
                        {pkg.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                              Most Popular
                            </span>
                          </div>
                        )}

                        <div className="text-center">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{pkg.name}</h4>
                          <div className="mb-4">
                            <span className="text-3xl font-bold text-blue-600">₹{pkg.price}</span>
                            <span className="text-gray-500 line-through ml-2">₹{pkg.originalPrice}</span>
                          </div>
                          <div className="text-sm text-green-600 mb-4">Save {pkg.savings}%</div>
                          
                          <div className="text-center mb-4">
                            <span className="text-2xl font-bold text-gray-900">{pkg.connects}</span>
                            <div className="text-sm text-gray-600">Connects</div>
                          </div>

                          <ul className="text-sm text-gray-600 space-y-1 mb-4">
                            {pkg.features.map((feature, index) => (
                              <li key={index} className="flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                                {feature}
                              </li>
                            ))}
                          </ul>

                          <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                            Buy Now
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements Tab */}
              {activeTab === 'achievements' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {achievements.map((achievement) => (
                      <div
                        key={achievement.id}
                        className={`p-4 rounded-lg border ${
                          achievement.earned
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl">{achievement.icon}</span>
                          {achievement.earned && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{achievement.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">{achievement.description}</p>
                        {achievement.earned ? (
                          <p className="text-xs text-green-600">Earned on {achievement.date}</p>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Progress</span>
                              <span>{achievement.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${achievement.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Rides Tab */}
              {activeTab === 'rides' && (
                <div className="space-y-4">
                  {recentRides.map((ride) => (
                    <div key={ride.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Car className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {ride.from} → {ride.to}
                          </div>
                          <div className="text-sm text-gray-600">
                            {ride.date} at {ride.time}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">₹{ride.price}</div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Star className="w-4 h-4 text-yellow-400 mr-1" />
                          {ride.rating}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Aadhar Verification Modal */}
      {showAadharVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Verify Aadhar</h3>
            <p className="text-gray-600 mb-4">
              Enter the OTP sent to your registered mobile number for Aadhar verification.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">OTP</label>
              <input
                type="text"
                value={aadharOtp}
                onChange={(e) => setAadharOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAadharVerification}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Verify
              </button>
              <button
                onClick={() => {
                  setShowAadharVerification(false);
                  setAadharOtp('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
            <div className="mt-4 text-xs text-gray-500 text-center">
              <p>Development Mode: Use OTP <strong>123456</strong></p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Complete Purchase</h3>
            <div className="mb-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold">{selectedPackage.name}</h4>
                <p className="text-gray-600">{selectedPackage.connects} Connects</p>
                <p className="text-2xl font-bold text-blue-600">₹{selectedPackage.price}</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="wallet">LinkCab Wallet</option>
                <option value="card">Credit/Debit Card</option>
                <option value="upi">UPI</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>
            {paymentMethod === 'razorpay' ? (
              <RazorpayButton
                amount={selectedPackage.price}
                receipt={`connect_pack_${selectedPackage.id}_${Date.now()}`}
                onSuccess={async (response) => {
                  setBuying(true);
                  setBuyError('');
                  try {
                    // Verify payment with backend
                    const verifyRes = await fetchWithAuth('/api/payments/verify', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        orderId: response.razorpay_order_id,
                        paymentId: response.razorpay_payment_id,
                        signature: response.razorpay_signature
                      })
                    });
                    const verifyData = await verifyRes.json();
                    if (verifyRes.ok) {
                      setBuySuccess(true);
                      setUser((u) => ({ ...u, connects: (u.connects || 0) + (verifyData.connectsAdded || selectedPackage.connects) }));
                      toast.success('Connects added successfully!');
                      setShowPaymentModal(false);
                      setSelectedPackage(null);
                    } else {
                      setBuyError(verifyData.error || 'Payment verification failed');
                    }
                  } catch (e) {
                    setBuyError('Payment verification failed');
                  } finally {
                    setBuying(false);
                  }
                }}
                onFailure={() => setBuyError('Payment failed or cancelled')}
              />
            ) : (
              <>
                <button
                  onClick={handleBuyConnects}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Pay ₹{selectedPackage.price}
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedPackage(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            {buying && <div className="mt-2 text-blue-600">Processing payment...</div>}
            {buySuccess && <div className="mt-2 text-green-600">Connects added successfully!</div>}
            {buyError && <div className="mt-2 text-red-600">{buyError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 